import React, { createContext, forwardRef, useCallback, useContext, useMemo, } from 'react';
import { FlatList as RNFlatList, ScrollView as RNScrollView, } from 'react-native';
import { FlashList } from '@shopify/flash-list';
import Animated, { useSharedValue, } from 'react-native-reanimated';
import { SCROLLABLE_TYPE, createBottomSheetScrollableComponent, useBottomSheetInternal, useScrollEventsHandlersDefault, } from 'utils/packages/bottomSheet';
/**
 * The scroll offset Modal reads for the separator under its title: its own, or
 * the `scrollable.scrollValue` passed to `Modal.show`.
 */
export const ModalScrollOffsetContext = createContext(null);
/**
 * gorhom's default scroll handlers, plus two things:
 * - the scroll offset goes to Modal's scroll offset on the UI thread, for the
 *   separator under the title;
 * - a nested call returns at once. While the scrollable is locked, the defaults
 *   call `scrollTo`, which Fabric on iOS answers with a scroll event on the same
 *   stack, which calls `scrollTo` again until the stack overflows
 *   (gorhom/react-native-bottom-sheet#2737, unfixed in 5.2.14).
 */
const useModalScrollEventsHandlers = (ref, contentOffsetY) => {
    const scrollOffset = useContext(ModalScrollOffsetContext);
    const defaults = useScrollEventsHandlersDefault(ref, contentOffsetY);
    const handling = useSharedValue(false);
    const guard = useCallback((handler) => (payload, context) => {
        'worklet';
        if (handling.value) {
            return;
        }
        handling.value = true;
        try {
            handler?.(payload, context);
        }
        finally {
            handling.value = false;
        }
    }, [handling]);
    const { handleOnScroll, handleOnEndDrag, handleOnMomentumEnd } = defaults;
    const guardedOnScroll = useMemo(() => guard(handleOnScroll), [guard, handleOnScroll]);
    const onEndDrag = useMemo(() => guard(handleOnEndDrag), [guard, handleOnEndDrag]);
    const onMomentumEnd = useMemo(() => guard(handleOnMomentumEnd), [guard, handleOnMomentumEnd]);
    const onScroll = useCallback((payload, context) => {
        'worklet';
        if (scrollOffset) {
            scrollOffset.value = payload.contentOffset.y;
        }
        guardedOnScroll(payload, context);
    }, [scrollOffset, guardedOnScroll]);
    return {
        ...defaults,
        handleOnScroll: onScroll,
        handleOnEndDrag: onEndDrag,
        handleOnMomentumEnd: onMomentumEnd,
    };
};
/**
 * gorhom's scrollables report the height of everything they have rendered as
 * the sheet's content height, and again as each batch of rows renders. Modal's
 * sheet sizes itself from its measured content, which includes the title and
 * the list's maxHeight; a list's report sent the sheet towards the wrong height
 * while it opened. This passes the list only the caller's onContentSizeChange.
 */
const withOwnContentSize = (Component) => forwardRef(({ ownOnContentSizeChange, ...props }, ref) => (<Component {...props} ref={ref} onContentSizeChange={ownOnContentSizeChange}/>));
const SheetScrollView = createBottomSheetScrollableComponent(SCROLLABLE_TYPE.SCROLLVIEW, withOwnContentSize(Animated.createAnimatedComponent(RNScrollView)));
const SheetFlatList = createBottomSheetScrollableComponent(SCROLLABLE_TYPE.FLATLIST, withOwnContentSize(Animated.createAnimatedComponent(RNFlatList)));
// FlashList's scroll view in the sheet.
const SheetFlashListScrollView = forwardRef(({ data: _data, onContentSizeChange, ...props }, ref) => (<SheetScrollView {...props} ref={ref} ownOnContentSizeChange={onContentSizeChange} scrollEventsHandlersHook={useModalScrollEventsHandlers}/>));
// gorhom's scrollables only work inside a sheet. Outside one (web pop-ups,
// screens) the components below render the plain Reanimated list, with the
// caller's own onScroll.
const useIsInSheet = () => useBottomSheetInternal(true) !== null;
/**
 * A scroll view for content shown with design-system Modal. In the sheet it is
 * gorhom's, so it scrolls on Android and hands a downward drag at the top over
 * to the sheet. There `onScroll` is not called: the offset goes to Modal's
 * `scrollable.scrollValue` instead.
 */
export const ModalScrollView = ({ onScroll, scrollEventThrottle, decelerationRate, onContentSizeChange, ...props }) => {
    const isInSheet = useIsInSheet();
    if (!isInSheet) {
        return (<Animated.ScrollView {...props} onScroll={onScroll} scrollEventThrottle={scrollEventThrottle} decelerationRate={decelerationRate} onContentSizeChange={onContentSizeChange}/>);
    }
    return (<SheetScrollView {...props} ownOnContentSizeChange={onContentSizeChange} scrollEventsHandlersHook={useModalScrollEventsHandlers}/>);
};
const INITIAL_NUM_TO_RENDER = 20;
/** `Animated.FlatList` for content shown with design-system Modal, as `ModalScrollView`. */
export const ModalFlatList = ({ onScroll, scrollEventThrottle, decelerationRate, onContentSizeChange, ...props }) => {
    const isInSheet = useIsInSheet();
    if (!isInSheet) {
        return (<Animated.FlatList {...props} onScroll={onScroll} scrollEventThrottle={scrollEventThrottle} decelerationRate={decelerationRate} onContentSizeChange={onContentSizeChange}/>);
    }
    // FlatList renders 10 rows, then the rest in batches. Each batch can grow the
    // sheet while it opens, and gorhom then starts the opening slide again from
    // where the sheet is. Modal's lists are short pickers, so render them whole.
    return (<SheetFlatList initialNumToRender={INITIAL_NUM_TO_RENDER} {...props} ownOnContentSizeChange={onContentSizeChange} scrollEventsHandlersHook={useModalScrollEventsHandlers}/>);
};
const AnimatedFlashList = Animated.createAnimatedComponent(FlashList);
/** FlashList for content shown with design-system Modal, as `ModalScrollView`. */
export const ModalFlashList = ({ onScroll, scrollEventThrottle, ...props }) => {
    const isInSheet = useIsInSheet();
    if (!isInSheet) {
        return (<AnimatedFlashList {...props} onScroll={onScroll} scrollEventThrottle={scrollEventThrottle}/>);
    }
    return (<FlashList {...props} renderScrollComponent={SheetFlashListScrollView}/>);
};
